import React, {
    createContext,
    useContext,
    useState,
    useCallback,
    useEffect,
    ReactNode,
} from 'react';
import * as pinService from '../services/pin';

interface PINState {
    isPINSetup: boolean;
    isPINSkipped: boolean;
    isPINSetupCompleted: boolean; // True if user has ever set up or skipped PIN (persists in Keychain)
    isLocked: boolean;
    isLoading: boolean;
    failedAttempts: number;
    lockoutUntil: Date | null;
}

interface PINContextType extends PINState {
    setupPIN: (pin: string) => Promise<boolean>;
    verifyPIN: (pin: string) => Promise<boolean>;
    changePIN: (oldPIN: string, newPIN: string) => Promise<boolean>;
    skipPINSetup: () => Promise<void>;
    clearPIN: () => Promise<void>;
    lockApp: () => void;
    unlockApp: () => void;
    resetFailedAttempts: () => void;
    isLockedOut: () => boolean;
    getRemainingLockoutTime: () => number;
    refreshPINState: () => Promise<void>;
}

const initialState: PINState = {
    isPINSetup: false,
    isPINSkipped: false,
    isPINSetupCompleted: false,
    isLocked: false,
    isLoading: true,
    failedAttempts: 0,
    lockoutUntil: null,
};

const PINContext = createContext<PINContextType | undefined>(undefined);

export const usePIN = (): PINContextType => {
    const context = useContext(PINContext);
    if (!context) {
        throw new Error('usePIN must be used within a PINProvider');
    }
    return context;
};

interface PINProviderProps {
    children: ReactNode;
}

export const PINProvider: React.FC<PINProviderProps> = ({ children }) => {
    const [state, setState] = useState<PINState>(initialState);

    // Initialize PIN state on mount
    useEffect(() => {
        const initializePIN = async () => {
            try {
                const [hasPIN, hasPINSkipped, hasSetupCompleted, attempts] = await Promise.all([
                    pinService.hasPIN(),
                    pinService.hasPINSkipped(),
                    pinService.hasPINSetupCompleted(),
                    pinService.loadPINAttempts(),
                ]);

                setState(prev => ({
                    ...prev,
                    isPINSetup: hasPIN,
                    isPINSkipped: hasPINSkipped,
                    isPINSetupCompleted: hasSetupCompleted,
                    isLocked: hasPIN, // Lock app if PIN is set up
                    // The attempts and lockout survive a restart: killing the
                    // app between tries no longer starts the count again.
                    failedAttempts: hasPIN ? attempts.failedAttempts : 0,
                    lockoutUntil: hasPIN ? attempts.lockoutUntil : null,
                    isLoading: false,
                }));
            } catch (error) {
                console.error('Error initializing PIN state:', error);
                setState(prev => ({ ...prev, isLoading: false }));
            }
        };

        initializePIN();
    }, []);

    const refreshPINState = useCallback(async () => {
        try {
            const [hasPIN, hasPINSkipped, hasSetupCompleted] = await Promise.all([
                pinService.hasPIN(),
                pinService.hasPINSkipped(),
                pinService.hasPINSetupCompleted(),
            ]);

            setState(prev => ({
                ...prev,
                isPINSetup: hasPIN,
                isPINSkipped: hasPINSkipped,
                isPINSetupCompleted: hasSetupCompleted,
            }));
        } catch (error) {
            console.error('Error refreshing PIN state:', error);
        }
    }, []);

    const setupPIN = useCallback(async (pin: string): Promise<boolean> => {
        const success = await pinService.savePIN(pin);
        if (success) {
            await pinService.savePINAttempts({ failedAttempts: 0, lockoutUntil: null });
            setState(prev => ({
                ...prev,
                isPINSetup: true,
                isPINSkipped: false,
                isPINSetupCompleted: true,
                isLocked: false, // Don't lock immediately after setup
            }));
        }
        return success;
    }, []);

    const verifyPIN = useCallback(async (pin: string): Promise<boolean> => {
        // Check if currently locked out
        if (state.lockoutUntil && new Date() < state.lockoutUntil) {
            return false;
        }

        const isValid = await pinService.verifyPIN(pin);

        if (isValid) {
            await pinService.savePINAttempts({ failedAttempts: 0, lockoutUntil: null });
            setState(prev => ({
                ...prev,
                isLocked: false,
                failedAttempts: 0,
                lockoutUntil: null,
            }));
        } else {
            const newFailedAttempts = state.failedAttempts + 1;
            const lockoutDuration = pinService.getLockoutDuration(newFailedAttempts);
            const lockoutUntil = lockoutDuration > 0
                ? new Date(Date.now() + lockoutDuration)
                : null;

            // Stored before the screen hears of it, so a kill right after a
            // wrong try cannot undo it.
            await pinService.savePINAttempts({ failedAttempts: newFailedAttempts, lockoutUntil });
            setState(prev => ({
                ...prev,
                failedAttempts: newFailedAttempts,
                lockoutUntil,
            }));
        }

        return isValid;
    }, [state.failedAttempts, state.lockoutUntil]);

    const changePIN = useCallback(async (oldPIN: string, newPIN: string): Promise<boolean> => {
        return await pinService.changePIN(oldPIN, newPIN);
    }, []);

    const skipPINSetup = useCallback(async () => {
        await pinService.skipPINSetup();
        setState(prev => ({
            ...prev,
            isPINSkipped: true,
            isPINSetupCompleted: true,
            isLocked: false,
        }));
    }, []);

    const clearPIN = useCallback(async () => {
        await pinService.clearPIN();
        setState(prev => ({
            ...prev,
            isPINSetup: false,
            isPINSkipped: false,
            isPINSetupCompleted: false,
            isLocked: false,
            failedAttempts: 0,
            lockoutUntil: null,
        }));
    }, []);

    const lockApp = useCallback(() => {
        // Only lock if PIN is set up
        if (state.isPINSetup) {
            setState(prev => ({ ...prev, isLocked: true }));
        }
    }, [state.isPINSetup]);

    const unlockApp = useCallback(() => {
        pinService.savePINAttempts({ failedAttempts: 0, lockoutUntil: null });
        setState(prev => ({
            ...prev,
            isLocked: false,
            failedAttempts: 0,
            lockoutUntil: null,
        }));
    }, []);

    const resetFailedAttempts = useCallback(() => {
        pinService.savePINAttempts({ failedAttempts: 0, lockoutUntil: null });
        setState(prev => ({
            ...prev,
            failedAttempts: 0,
            lockoutUntil: null,
        }));
    }, []);

    const isLockedOut = useCallback((): boolean => {
        return pinService.isLockedOut(state.lockoutUntil);
    }, [state.lockoutUntil]);

    const getRemainingLockoutTime = useCallback((): number => {
        return pinService.getRemainingLockoutTime(state.lockoutUntil);
    }, [state.lockoutUntil]);

    const value: PINContextType = {
        ...state,
        setupPIN,
        verifyPIN,
        changePIN,
        skipPINSetup,
        clearPIN,
        lockApp,
        unlockApp,
        resetFailedAttempts,
        isLockedOut,
        getRemainingLockoutTime,
        refreshPINState,
    };

    return <PINContext.Provider value={value}>{children}</PINContext.Provider>;
};

export default PINContext;

